/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  Typography,
  RACSearchDropdown,
} from '@rentacenter/racstrap';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ProcessSuccess } from '../../assets/images/validation-success.svg';
import { ReactComponent as ProcessFailed } from '../../images/info-button-svg.svg';
import { ReactComponent as ProcessSpinner } from '../../assets/images/Spinner.svg';
import { ReactComponent as InfoIconBlue } from '../../assets/images/info-icon-blue.svg';
import { ComboBoxData, PricingData } from '../../types/types';
import { getPricing } from '../../api/user';
import { emptyField } from '../../constants/constants';

export type EligibilityDecisionType = 'NONE' | 'PROCESSING' | 'LEASABLE' | 'NOT_LEASABLE' | 'APPROVED' | 'CONDITIONAL' | 'DENIED';

export interface ConditionalProduct {
  productName: string;
  unitCost: string;
  quantity: string;
  // If already auto-priced via catalog match, these are pre-filled
  autoPriced?: boolean;
  rmsItemNumber?: string;
  weeklyRate?: string;
  totalCost?: string;
}

export interface EligibilityDecisionData {
  decision: EligibilityDecisionType;
  vendorName: string;
  totalCost: string;
  errorMessage?: string;
  classifications?: any;
  products?: ConditionalProduct[];
}

export interface EligibilityDecisionProps {
  data: EligibilityDecisionData;
  onPricingResolved?: (pricingInfo: { weeklyRate: string; totalCost: string; rmsItemNumber: string; productIndex?: number; allResolved?: boolean; resolvedProducts?: Array<{ rmsItemNumber: string; weeklyRate: string; totalCost: string }> }) => void;
}

const FONT_CHOICE = 'OpenSans-semibold';

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    height: '100%',
    padding: '0.5rem 0.75rem',
    overflowY: 'auto' as const,
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '0.25rem',
  },
  successIcon: {
    width: '28px',
    height: '28px',
  },
  failedIcon: {
    width: '28px',
    height: '28px',
  },
  spinnerIcon: {
    width: '40px',
    height: '40px',
  },
  decisionTitle: {
    fontSize: '0.875rem',
    fontFamily: FONT_CHOICE,
    fontWeight: 700,
    textAlign: 'center' as const,
    marginBottom: '0.25rem',
  },
  decisionMessage: {
    fontSize: '0.7rem',
    fontFamily: 'OpenSans-regular',
    textAlign: 'center' as const,
    color: '#333333',
    marginBottom: '0.375rem',
    lineHeight: 1.3,
  },
  transactionCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    backgroundColor: '#E8F1FE',
    borderRadius: '6px',
    padding: '0.375rem 0.75rem',
    width: '100%',
    marginTop: '0.375rem',
  },
  transactionLabel: {
    fontSize: '0.65rem',
    fontFamily: FONT_CHOICE,
    color: '#333333',
  },
  transactionAmount: {
    fontSize: '0.875rem',
    fontFamily: FONT_CHOICE,
    fontWeight: 700,
    color: '#111111',
  },
  transactionTax: {
    fontSize: '0.7rem',
    color: '#666666',
    textAlign: 'center' as const,
  },
  infoNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    marginTop: '0.75rem',
    width: '100%',
  },
  infoIcon: {
    width: '16px',
    height: '16px',
    flexShrink: 0,
    color: '#2468FF',
    fill: '#2468FF',
    marginTop: '2px',
  },
  infoText: {
    fontSize: '0.75rem',
    color: '#333333',
    lineHeight: 1.4,
  },
  conditionalSection: {
    width: '100%',
    marginTop: '0.5rem',
    padding: '0 0.5rem',
  },
  dropdownRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.75rem',
    width: '100%',
  },
  comboWrapper: {
    flex: 1,
    '& .MuiFormControl-root': {
      width: '100%',
    },
    '& .MuiInputBase-root': {
      width: '100%',
      fontSize: '0.7rem',
      height: '32px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.7rem',
    },
    '& .MuiSelect-select': {
      fontSize: '0.7rem',
      padding: '4px 8px',
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '0.7rem',
      padding: '6px 8px',
    },
    '& .MuiFormLabel-root': {
      fontSize: '0.7rem',
    },
    '& label': {
      fontSize: '0.7rem',
    },
    '& .MuiMenuItem-root': {
      fontSize: '0.7rem',
      minHeight: '28px',
      padding: '4px 8px',
    },
    '& .MuiListItem-root': {
      fontSize: '0.7rem',
      minHeight: '28px',
      padding: '4px 8px',
    },
    '& .MuiAutocomplete-option': {
      fontSize: '0.7rem',
      minHeight: '28px',
      padding: '4px 8px',
    },
  },
  placeholderRoot: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    color: '#999',
  },
  placeholderText: {
    fontSize: '0.875rem',
    textAlign: 'center' as const,
  },
  processingRoot: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  processingText: {
    fontSize: '0.875rem',
    color: '#666',
    textAlign: 'center' as const,
    marginTop: '0.5rem',
  },
}));

// ─── Per-product conditional approval component ───────────────────────────────
// Renders one set of dept/bracket dropdowns per product so each gets its own
// rmsItemNumber from the pricing service.
// If the product is already auto-priced (autoPriced=true), shows resolved state instead.
const ProductBracketSelector = ({
  productIndex,
  productName,
  unitCost,
  quantity,
  classifications,
  onResolved,
  classes,
  t,
  // Auto-priced props — if set, skip dropdowns and show resolved immediately
  autoPriced,
  autoPricedData,
  modelNumber = '',
}: any) => {
  const emptyF = emptyField;

  const [departments, setDepartments] = React.useState<ComboBoxData[]>([emptyF]);
  const [subdepartments, setSubDepartments] = React.useState<ComboBoxData[]>([emptyF]);
  const [brakets, setBrakets] = React.useState<ComboBoxData[]>([emptyF]);
  const [typeOptions, setTypeOptions] = React.useState<ComboBoxData[]>([emptyF]);
  const [subTypeOptions, setSubTypeOptions] = React.useState<ComboBoxData[]>([emptyF]);
  const [selDept, setSelDept] = React.useState<ComboBoxData>(emptyF);
  const [selSubDept, setSelSubDept] = React.useState<ComboBoxData>(emptyF);
  const [selBraket, setSelBraket] = React.useState<ComboBoxData>(emptyF);
  const [selType, setSelType] = React.useState<ComboBoxData>(emptyF);
  const [selSubType, setSelSubType] = React.useState<ComboBoxData>(emptyF);
  const [loading, setLoading] = React.useState(false);

  // If auto-priced, fire onResolved immediately on mount
  React.useEffect(() => {
    if (autoPriced && autoPricedData) {
      console.log('[LocalSourcing] ProductBracketSelector auto-priced onResolved:', JSON.stringify({ ...autoPricedData, productIndex, quantity }, null, 2));
      onResolved({ ...autoPricedData, productIndex, quantity });
    }
  }, []);

  // If already auto-priced — show resolved state, no dropdowns needed
  if (autoPriced && autoPricedData) {
    return (
      <div style={{ borderLeft: '3px solid #2E7D32', paddingLeft: '0.75rem', marginBottom: '0.75rem', width: '100%' }}>
        <Typography style={{ fontSize: '0.75rem', fontWeight: 700, color: '#111', marginBottom: '4px' }}>
          {`Product ${productIndex + 1}: ${productName}`}
          <span style={{ fontWeight: 400, color: '#555', marginLeft: '0.5rem' }}>
            {`$${unitCost} × ${quantity}`}
          </span>
        </Typography>
        <Typography style={{ fontSize: '0.75rem', color: '#2E7D32', fontWeight: 600 }}>
          {`✓ Pricing retrieved — Weekly: $${autoPricedData.weeklyRate} | Total: $${autoPricedData.totalCost}`}
        </Typography>
        {autoPricedData.rmsItemNumber && (
          <Typography style={{ fontSize: '0.7rem', color: '#666' }}>
            {`RMS Item: ${autoPricedData.rmsItemNumber}`}
          </Typography>
        )}
      </div>
    );
  }
  const [error, setError] = React.useState('');
  const [resolved, setResolved] = React.useState<any>(null);

  React.useEffect(() => {
    if (classifications) {
      const depList: ComboBoxData[] = classifications.map((item: any) => ({ value: item.id, label: item.name }));
      setDepartments(depList);
      // Auto-select if only one option
      if (depList.length === 1) {
        setTimeout(() => onDept(depList[0]), 0);
      }
    }
  }, [classifications]);

  React.useEffect(() => {
    if (selDept.value && selSubDept.value && selBraket.value) {
      fetchPricing();
    }
  }, [selBraket.value]);

  const getById = (arr: any[], id: string) => arr && arr.filter(({ id: i }: any) => id.split('.')[0] === i);

  const onDept = (item: ComboBoxData) => {
    setSelDept(item); setSelSubDept(emptyF); setSelBraket(emptyF); setSelType(emptyF); setSelSubType(emptyF);
    setSubDepartments([emptyF]); setBrakets([emptyF]); setTypeOptions([emptyF]); setSubTypeOptions([emptyF]);
    const out: any = getById(classifications, item.value);
    if (out?.[0]?.subDepartments) {
      const list = out[0].subDepartments.map((s: any) => ({ value: s.id, label: s.name }));
      setSubDepartments(list);
      // Pass item directly to avoid stale state
      if (list.length === 1) setTimeout(() => onSubDeptWithDept(list[0], item), 0);
    }
  };

  // Explicit dept param to avoid stale selDept state during auto-select chain
  const onSubDeptWithDept = (item: ComboBoxData, deptItem: ComboBoxData) => {
    setSelSubDept(item); setSelBraket(emptyF); setSelType(emptyF); setSelSubType(emptyF);
    setBrakets([emptyF]); setTypeOptions([emptyF]); setSubTypeOptions([emptyF]);
    const subList: any = getById(classifications, deptItem.value);
    const brakList: any = getById(subList?.[0]?.subDepartments, item.value);
    if (brakList?.[0]?.brackets) {
      const list = brakList[0].brackets.map((b: any) => ({ value: b.id, label: b.name }));
      setBrakets(list);
      if (list.length === 1) setTimeout(() => onBraketWithParents(list[0], deptItem, item), 0);
    }
  };

  const onSubDept = (item: ComboBoxData) => onSubDeptWithDept(item, selDept);

  const onBraketWithParents = (item: ComboBoxData, deptItem: ComboBoxData, subDeptItem: ComboBoxData) => {
    setSelBraket(item); setSelType(emptyF); setSelSubType(emptyF);
    setTypeOptions([emptyF]); setSubTypeOptions([emptyF]);
    const subList: any = getById(classifications, deptItem.value);
    const brakList: any = getById(subList?.[0]?.subDepartments, subDeptItem.value);
    const bracket = brakList?.[0]?.brackets?.find((b: any) => b.id === item.value);
    if (bracket?.types) {
      const list = bracket.types.map((tp: any) => ({ value: tp.id, label: tp.name || tp.id }));
      setTypeOptions(list);
      
      if (list.length === 1) {
        setSelType(list[0]);
        const subType = bracket.types[0].subTypes.map((st: any) => ({ value: st.id, label: st.name || st.id }));
        setSelSubType(subType[0] );
      }
    }
  };

  const onBraket = (item: ComboBoxData) => onBraketWithParents(item, selDept, selSubDept);

  const onTypeWithParents = (item: ComboBoxData, deptItem: ComboBoxData, subDeptItem: ComboBoxData, braketItem: ComboBoxData) => {
    setSelType(item); setSelSubType(emptyF); setSubTypeOptions([emptyF]);
    const subList: any = getById(classifications, deptItem.value);
    const brakList: any = getById(subList?.[0]?.subDepartments, subDeptItem.value);
    const bracket = brakList?.[0]?.brackets?.find((b: any) => b.id === braketItem.value);
    const type = bracket?.types?.find((tp: any) => tp.id === item.value);
    if (type?.subTypes) {
      const list = type.subTypes.map((st: any) => ({ value: st.id, label: st.name || st.id }));
      setSubTypeOptions(list);
      if (list.length === 1) setTimeout(() => setSelSubType(list[0]), 0);
    }
  };

  const onType = (item: ComboBoxData) => onTypeWithParents(item, selDept, selSubDept, selBraket);

  const fetchPricing = async () => {
    setLoading(true); setError(''); setResolved(null);
    try {

      const other = '';
      const payload = {
        bracket: selBraket.value,
        costPerItem: unitCost,
        department: selDept.value,
        itemSubType: selSubType.value,
        itemType: selType.value,
        poType: 'MANUAL',
        rmsItemNumber: other,
        storeNumber: window.sessionStorage.getItem('storeNumber'),
        subDepartment: selSubDept.value,
      };
      const response = await getPricing(payload);      
      const responseData = response?.data || response;
      const pricedItems = responseData?.result || [];
      // Check for backend error (PCM_NF001 - No matching products)
      const backendErrors = responseData?.errors || response?.data?.errors;
      if (backendErrors?.length > 0) {
        setError(`No matching product found. Please try a different bracket.`);
        
      } else if (pricedItems.length > 0 && pricedItems[0]?.newRate) {
        const priced = pricedItems[0];
        const weeklyRate = priced.newRate.weeklyRate || '';
        const totalCost = priced.newRate.totalCost || '';
        const rmsItemNumber = priced.rmsItemNumber ? String(priced.rmsItemNumber) : '';
        const weeklyTerm = priced.newRate.weeklyTerm ? String(priced.newRate.weeklyTerm) : '';
        const cashPrice = priced.newRate.cashPrice || totalCost;
        const brand =  '';
        if (weeklyRate && totalCost) {
          const info = { weeklyRate, totalCost, rmsItemNumber, weeklyTerm, cashPrice, brand, productIndex, quantity, model: productName, productName, modelNumber: rmsItemNumber, costPerItem: unitCost };
          console.log('[LocalSourcing] ProductBracketSelector manual pricing resolved:', JSON.stringify(info, null, 2));
          setResolved(info);
          onResolved(info);
        } else { setError('No pricing found for this selection. Please try a different bracket.'); }
      } else { setError('No matching product found. Please try a different bracket.'); }
    } catch { setError('Unable to retrieve pricing. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ borderLeft: '3px solid #2468FF', paddingLeft: '0.75rem', marginBottom: '0.75rem', width: '100%' }}>
      <Typography style={{ fontSize: '0.75rem', fontWeight: 700, color: '#111', marginBottom: '0.375rem' }}>
        {`Product ${productIndex + 1}: ${productName}`}
        <span style={{ fontWeight: 400, color: '#555', marginLeft: '0.5rem' }}>
          {`$${unitCost} × ${quantity}`}
        </span>
      </Typography>
      <div className={classes.dropdownRow}>
        <div className={classes.comboWrapper}>
          <RACSearchDropdown name={`dept-${productIndex}`} inputLabel="Department" options={departments} OnChange={onDept} required disabled={false} value={selDept} />
        </div>
        <div className={classes.comboWrapper}>
          <RACSearchDropdown name={`subdept-${productIndex}`} inputLabel="Sub Department" options={subdepartments} OnChange={onSubDept} required disabled={!selDept.value} value={selSubDept} />
        </div>
        <div className={classes.comboWrapper}>
          <RACSearchDropdown name={`braket-${productIndex}`} inputLabel="Bracket" options={brakets} OnChange={onBraket} required disabled={!selSubDept.value} value={selBraket} />
        </div>
      </div>
      {loading && <Typography style={{ fontSize: '0.75rem', color: '#666' }}>Retrieving pricing...</Typography>}
      {error && <Typography style={{ fontSize: '0.75rem', color: '#C62828' }}>{error}</Typography>}
      {resolved && (
        <Typography style={{ fontSize: '0.75rem', color: '#2E7D32' }}>
          ✓ {`Weekly: $${resolved.weeklyRate} | Total: $${resolved.totalCost}`}
        </Typography>
      )}
    </div>
  );
};

const ConditionalPerProduct = ({ data, productCount, productList, onPricingResolved, classes, t }: any) => {
  const resolvedMap = React.useRef<Record<number, any>>({});
  const [resolvedTotal, setResolvedTotal] = React.useState<string>('');
  const [resolvedWeekly, setResolvedWeekly] = React.useState<string>('');
  const [fundingInProgress, setFundingInProgress] = React.useState<boolean>(false);
  // data.classifications may be the raw array OR { classifications: [], products: [] }
  const rawClassifications = Array.isArray(data.classifications)
    ? data.classifications
    : (data.classifications?.classifications || []);

  const handleProductResolved = (info: any) => {
    resolvedMap.current[info.productIndex] = info;

    // Update running total after each product resolves
    const resolvedSoFar = Object.values(resolvedMap.current);
    const runningTotal = resolvedSoFar.reduce(
      (sum: number, p: any) => sum + parseFloat(p.totalCost || '0'), 0
    );
    const runningWeekly = resolvedSoFar.reduce(
      (sum: number, p: any) => sum + parseFloat(p.weeklyRate || '0'), 0
    );
    setResolvedTotal(runningTotal.toFixed(2));
    setResolvedWeekly(runningWeekly.toFixed(2));

    // Fire onPricingResolved only when ALL products are resolved
    if (Object.keys(resolvedMap.current).length === productCount) {
      const allResolved = Object.values(resolvedMap.current);
      console.log('[LocalSourcing] All products resolved, firing onPricingResolved:', JSON.stringify(allResolved, null, 2));
      if (onPricingResolved) {
        // Show funding in progress INSIDE the conditional panel — keeps pricing visible
        setFundingInProgress(true);
        // AC 2: Pass the accurate sum from all products' pricing responses
        onPricingResolved({
          weeklyRate: String(runningWeekly.toFixed(2)),
          totalCost: String(runningTotal.toFixed(2)),
          rmsItemNumber: allResolved[0]?.rmsItemNumber || '',
          allResolved: true,
          resolvedProducts: allResolved,
        });
      }
    }
  };

  return (
    <div className={classes.root}>

      <div className={classes.iconContainer}>
        <ProcessSuccess className={classes.successIcon} />
      </div>
      <Typography className={classes.decisionTitle}>{t('Conditional Approval')}</Typography>
      <Typography className={classes.decisionMessage}>
        {t('Please select Department, Sub Department, and Bracket for each product to retrieve pricing.')}
      </Typography>
      <div className={classes.conditionalSection}>
        {productCount > 0
          ? productList.map((p: any, i: number) => (
              <ProductBracketSelector
                key={i}
                productIndex={i}
                productName={p.productName || `Product ${i + 1}`}
                unitCost={p.unitCost || '0'}
                quantity={p.quantity || '1'}
                classifications={rawClassifications}
                onResolved={handleProductResolved}
                classes={classes}
                t={t}
                autoPriced={p.autoPriced || false}
                autoPricedData={p.autoPriced ? { rmsItemNumber: p.rmsItemNumber, weeklyRate: p.weeklyRate, weeklyTerm: p.weeklyTerm || '', totalCost: p.totalCost, cashPrice: p.cashPrice || '', model: p.model || p.productName || '', brand: p.brand || '', modelNumber: p.modelNumber || '', costPerItem: p.unitCost || p.costPerItem || '0', productName: p.productName || p.model || '' } : undefined}
                modelNumber={p.modelNumber || ''}
              />
            ))
          : <ProductBracketSelector
              productIndex={0}
              productName="Product 1"
              unitCost={data.totalCost || '0'}
              quantity="1"
              classifications={rawClassifications}
              onResolved={handleProductResolved}
              classes={classes}
              t={t}
            />
        }
      </div>

      {/* Transaction card — shows total only, no weekly payment */}
      <div className={classes.transactionCard}>
        <div style={{ flex: 1, textAlign: 'center' as const }}>
          <Typography className={classes.transactionLabel}>
            {t('Total Financing Request')}
          </Typography>
          <Typography className={classes.transactionAmount}>
            ${resolvedTotal || data.totalCost || '—'}
          </Typography>
          <Typography className={classes.transactionTax}>{t('(+ tax)')}</Typography>
        </div>
      </div>
    </div>
  );
};

export const EligibilityDecision = ({ data, onPricingResolved }: EligibilityDecisionProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // Conditional approval dropdowns state
  const [departments, setDepartments] = useState<ComboBoxData[]>([emptyField]);
  const [subdepartments, setSubDepartments] = useState<ComboBoxData[]>([emptyField]);
  const [brakets, setBrakets] = useState<ComboBoxData[]>([emptyField]);
  const [typeOptions, setTypeOptions] = useState<ComboBoxData[]>([emptyField]);
  const [subTypeOptions, setSubTypeOptions] = useState<ComboBoxData[]>([emptyField]);
  const [selectedDepartment, setSelectedDepartment] = useState<ComboBoxData>(emptyField);
  const [selectedSubDepartment, setSelectedSubDepartment] = useState<ComboBoxData>(emptyField);
  const [selectedBraket, setSelectedBraket] = useState<ComboBoxData>(emptyField);
  const [selectedTypeCombo, setSelectedTypeCombo] = useState<ComboBoxData>(emptyField);
  const [selectedSubTypeCombo, setSelectedSubTypeCombo] = useState<ComboBoxData>(emptyField);

  // Pricing state
  const [pricingLoading, setPricingLoading] = useState<boolean>(false);
  const [pricingInfo, setPricingInfo] = useState<{ weeklyRate: string; totalCost: string; rmsItemNumber: string } | null>(null);
  const [pricingError, setPricingError] = useState<string>('');

  // Initialize departments from classifications
  // Note: CONDITIONAL state is now handled by ConditionalPerProduct — this useEffect
  // Note: CONDITIONAL state is fully handled by ConditionalPerProduct component.
  // This useEffect is no longer needed for CONDITIONAL.
  useEffect(() => {
    // kept as no-op for safety
  }, [data.classifications, data.decision]);

  const getDataById = (dataArr: any[], parentId: string): any[] | undefined => {
    return dataArr && dataArr.filter(({ id }: { id: string }) => parentId.split('.')[0] === id);
  };

  const onDepartmentSelected = (selectedItem: ComboBoxData) => {
    setSelectedDepartment(selectedItem);
    setSelectedSubDepartment(emptyField);
    setSelectedBraket(emptyField);
    setSelectedTypeCombo(emptyField);
    setSelectedSubTypeCombo(emptyField);
    setSubDepartments([emptyField]);
    setBrakets([emptyField]);
    setTypeOptions([emptyField]);
    setSubTypeOptions([emptyField]);

    if (data.classifications && selectedItem.value) {
      const out: any = getDataById(data.classifications, selectedItem.value);
      if (out && out[0]?.subDepartments) {
        const subDepts: ComboBoxData[] = out[0].subDepartments.map((item: PricingData) => ({
          value: item.id,
          label: item.name,
        }));
        setSubDepartments(subDepts);
        // Auto-select if only one option
        if (subDepts.length === 1) {
          setTimeout(() => onSubDepartmentSelected(subDepts[0], selectedItem), 0);
        }
      }
    }
  };

  const onSubDepartmentSelected = (selectedItem: ComboBoxData, dept?: ComboBoxData) => {
    setSelectedSubDepartment(selectedItem);
    setSelectedBraket(emptyField);
    setSelectedTypeCombo(emptyField);
    setSelectedSubTypeCombo(emptyField);
    setBrakets([emptyField]);
    setTypeOptions([emptyField]);
    setSubTypeOptions([emptyField]);

    const deptValue = dept?.value || selectedDepartment.value;
    if (data.classifications && deptValue && selectedItem.value) {
      const subList = getDataById(data.classifications, deptValue);
      if (subList) {
        const braketsList: any = getDataById(subList[0]?.subDepartments, selectedItem.value);
        if (braketsList && braketsList[0]?.brackets) {
          const brakList: ComboBoxData[] = braketsList[0].brackets.map((item: PricingData) => ({
            value: item.id,
            label: item.name,
          }));
          setBrakets(brakList);
          // Auto-select if only one option
          if (brakList.length === 1) {
            setTimeout(() => onBracketSelected(brakList[0], deptValue, selectedItem.value), 0);
          }
        }
      }
    }
  };

  const onBracketSelected = (selectedItem: ComboBoxData, deptVal?: string, subDeptVal?: string) => {
    setSelectedBraket(selectedItem);
    setSelectedTypeCombo(emptyField);
    setSelectedSubTypeCombo(emptyField);
    setTypeOptions([emptyField]);
    setSubTypeOptions([emptyField]);

    const deptValue = deptVal || selectedDepartment.value;
    const subDeptValue = subDeptVal || selectedSubDepartment.value;

    if (data.classifications && deptValue && subDeptValue) {
      const subList = getDataById(data.classifications, deptValue);
      if (subList) {
        const braketsList: any = getDataById(subList[0]?.subDepartments, subDeptValue);
        if (braketsList && braketsList[0]?.brackets) {
          const bracket = braketsList[0].brackets.find((b: any) => b.id === selectedItem.value);
          if (bracket && bracket.types) {
            const types: ComboBoxData[] = bracket.types.map((item: any) => ({
              value: item.id,
              label: item.name || item.id,
            }));
            setTypeOptions(types);
            // Auto-select if only one option
            if (types.length === 1) {
              setTimeout(() => onTypeSelected(types[0], deptValue, subDeptValue, selectedItem.value), 0);
            }
          }
        }
      }
    }
  };

  const onTypeSelected = (selectedItem: ComboBoxData, deptVal?: string, subDeptVal?: string, braketVal?: string) => {
    setSelectedTypeCombo(selectedItem);
    setSelectedSubTypeCombo(emptyField);
    setSubTypeOptions([emptyField]);

    const deptValue = deptVal || selectedDepartment.value;
    const subDeptValue = subDeptVal || selectedSubDepartment.value;
    const braketValue = braketVal || selectedBraket.value;

    if (data.classifications && deptValue && subDeptValue && braketValue) {
      const subList = getDataById(data.classifications, deptValue);
      if (subList) {
        const braketsList: any = getDataById(subList[0]?.subDepartments, subDeptValue);
        if (braketsList && braketsList[0]?.brackets) {
          const bracket = braketsList[0].brackets.find((b: any) => b.id === braketValue);
          if (bracket && bracket.types) {
            const type = bracket.types.find((typeItem: any) => typeItem.id === selectedItem.value);
            if (type && type.subTypes) {
              const subTypes: ComboBoxData[] = type.subTypes.map((item: any) => ({
                value: item.id,
                label: item.name || item.id,
              }));
              setSubTypeOptions(subTypes);
              // Auto-select if only one option
              if (subTypes.length === 1) {
                setTimeout(() => setSelectedSubTypeCombo(subTypes[0]), 0);
              }
            }
          }
        }
      }
    }
  };

  const onSubTypeSelected = (selectedItem: ComboBoxData) => {
    setSelectedSubTypeCombo(selectedItem);
  };

  // Call pricing service when all required dropdowns are selected
  useEffect(() => {
    if (data.decision === 'CONDITIONAL' && selectedDepartment.value && selectedSubDepartment.value && selectedBraket.value && selectedTypeCombo.value && selectedSubTypeCombo.value) {
      fetchPricing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBraket.value, selectedTypeCombo.value, selectedSubTypeCombo.value]);

  const fetchPricing = async () => {
    setPricingLoading(true);
    setPricingError('');
    setPricingInfo(null);

    try {

      const other = '';
      const payload = {
        bracket: selectedBraket.value,
        costPerItem: data.totalCost,
        department: selectedDepartment.value,
        itemType: selectedTypeCombo.value,
        itemSubType: selectedSubTypeCombo.value,
        poType: 'MANUAL',
        rmsItemNumber: other,
        storeNumber: window.sessionStorage.getItem('storeNumber'),
        subDepartment: selectedSubDepartment.value,
      };
      const response = await getPricing(payload);
      const responseData = response?.data || response;
      const pricedItems = responseData?.result || [];

      // Check for backend error (PCM_NF001 - No matching products)
      const backendErrors = responseData?.errors || response?.data?.errors;
      if (backendErrors?.length > 0) {
        setPricingError(`No matching product found. Please try a different bracket.`);
      } else if (pricedItems.length > 0 && pricedItems[0]?.newRate) {
        const priced = pricedItems[0];
        const weeklyRate = priced.newRate.weeklyRate || '';
        const totalCost = priced.newRate.totalCost || '';
        const rmsItemNumber = priced.rmsItemNumber ? String(priced.rmsItemNumber) : '';

        if (weeklyRate && totalCost) {
          setPricingInfo({ weeklyRate, totalCost, rmsItemNumber });
          setPricingError('');
          if (onPricingResolved) {
            onPricingResolved({ weeklyRate, totalCost, rmsItemNumber });
          }
        } else {
          setPricingError('No pricing found for this selection. Please try a different bracket.');
        }
      } else {
        setPricingError('No matching product found. Please try a different bracket.');
      }
    } catch (err: any) {
      console.error('Pricing fetch error:', err);
      setPricingError('Unable to retrieve pricing. Please try a different selection.');
    } finally {
      setPricingLoading(false);
    }
  };
// Transaction Amount
  const renderTransactionCard = () => (
    <div className={classes.transactionCard}>
      {(data.totalCost && String(data.totalCost).trim()) ? ( <>
      <Typography className={classes.transactionLabel}>
        {t('Transaction Amount')}
      </Typography>
      <div>
        <Typography className={classes.transactionAmount}>
          ${data.totalCost}
        </Typography>
        <Typography className={classes.transactionTax}>
          {'(+ tax)'}
        </Typography>
      </div>
      </>) : (<Typography className={classes.transactionLabel}>
        {t('Calculating Amount')}
      </Typography>) }
    </div>
  );

  // NONE state - placeholder
  if (data.decision === 'NONE') {
    return (
      <div className={classes.placeholderRoot}>
        <Typography className={classes.placeholderText}>
          {t('Product details will appear here')}
        </Typography>
      </div>
    );
  }

  // PROCESSING state
  if (data.decision === 'PROCESSING') {
    return (
      <div className={classes.processingRoot}>
        <ProcessSpinner className={classes.spinnerIcon} />
        <Typography className={classes.processingText}>
          {data.errorMessage || t('Processing...')}
        </Typography>
      </div>
    );
  }

  // LEASABLE state - item passed leasability check, proceeding to next steps
  if (data.decision === 'LEASABLE') {
    return (
      <div className={classes.root}>
        <div className={classes.iconContainer}>
          <ProcessSuccess className={classes.successIcon} />
        </div>
        <Typography className={classes.decisionTitle}>
          {t('Item is Leasable')}
        </Typography>
        <Typography className={classes.decisionMessage}>
          {data.classifications?.weeklyRate
            ? t('The captured item is eligible for lease. Pricing information has been retrieved.')
            : t('The captured item has been evaluated and is eligible for lease. Proceeding to determine pricing...')}
        </Typography>
        {data.classifications?.weeklyRate ? (
          <div className={classes.transactionCard}>
            <div>
              <Typography className={classes.transactionLabel}>
                {t('Weekly Payment')}
              </Typography>
              <Typography className={classes.transactionAmount}>
                ${data.classifications.weeklyRate}
              </Typography>
            </div>
            <div>
              <Typography className={classes.transactionLabel}>
                {t('Total Financing Amount')}
              </Typography>
              <Typography className={classes.transactionAmount}>
                ${data.classifications.totalCost}
              </Typography>
            </div>
          </div>
        ) : (
          renderTransactionCard()
        )}
      </div>
    );
  }

  // NOT_LEASABLE state - item failed leasability check
  if (data.decision === 'NOT_LEASABLE') {
    const items: any[] = Array.isArray(data.classifications) ? data.classifications : [];
    return (
      <div className={classes.root}>
        <div className={classes.iconContainer}>
          <ProcessFailed className={classes.failedIcon} />
        </div>
        <Typography className={classes.decisionTitle}>
          {t('Item is Not Leasable')}
        </Typography>
        {items.length > 0 ? (
          <div style={{ width: '100%', marginTop: '0.375rem' }}>
            {items.map((item: any, index: number) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'flex-start',
                  padding: '0.375rem 0.5rem',
                  marginBottom: '0.375rem',
                  backgroundColor: '#FFF5F5',
                  borderLeft: '3px solid #C62828',
                  borderRadius: '4px',
                }}
              >
                <span style={{
                  minWidth: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#C62828',
                  color: '#fff',
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '1px',
                }}>
                  {index + 1}
                </span>
                <div>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#111', marginBottom: '2px' }}>
                    {item.description || `Product ${index + 1}`}
                  </Typography>
                  <Typography style={{ fontSize: '0.65rem', color: '#666' }}>
                    {item.reason || 'Not leasable'}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Typography className={classes.decisionMessage}>
            {data.errorMessage || t('The captured item is not eligible for lease. Please review the item and try again.')}
          </Typography>
        )}
      </div>
    );
  }

  // APPROVED state
  if (data.decision === 'APPROVED') {
    return (
      <div className={classes.root}>
        <div className={classes.iconContainer}>
          <ProcessSuccess className={classes.successIcon} />
        </div>
        <Typography className={classes.decisionTitle}>
          {t('Approval Successful!')}
        </Typography>
        <Typography className={classes.decisionMessage}>
          {t('Your prefund request has been approved.')}{' '}
          {t('Please proceed to complete your checkout at')} {data.vendorName}.
        </Typography>
        {renderTransactionCard()}
        <div className={classes.infoNote}>
          <InfoIconBlue className={classes.infoIcon} />
          <Typography className={classes.infoText}>
            {t('Please note: You will be required to upload tax-exempt documentation during the checkout process.')}
          </Typography>
        </div>
      </div>
    );
  }

  // CONDITIONAL state
  if (data.decision === 'CONDITIONAL') {
    // data.classifications may be { classifications: [], products: [] } or raw array
    const productList: any[] = Array.isArray(data.classifications)
      ? (data.products || [])
      : (data.classifications?.products || []);
    const productCount = productList.length || 1;

    return (
      <ConditionalPerProduct
        data={data}
        productCount={productCount}
        productList={productList}
        onPricingResolved={onPricingResolved}
        classes={classes}
        t={t}
      />
    );
  }

  // DENIED state
  if (data.decision === 'DENIED') {
    return (
      <div className={classes.root}>
        <div className={classes.iconContainer}>
          <ProcessFailed className={classes.failedIcon} />
        </div>
        <Typography className={classes.decisionTitle}>
          {t('Approval Declined')}
        </Typography>
        <Typography className={classes.decisionMessage}>
          {data.errorMessage || t("We're unable to process your prefund request at this time.")}
        </Typography>
      </div>
    );
  }

  return null;
};
